import { useEffect, useMemo, useRef, useState } from 'react';

import ResetIcon from '@/assets/all-icons/reset-icon';
import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicationSchedulePicker from '@/shared-components/forms/components/PublicationSchedulePicker';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import { clearPublicationSchedule } from '../../../publicationLifecycle';

import './styles/PublicationDatePopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

/**
 * Returns the timezone offset (in minutes) for the provided IANA timezone and date.
 * Sign follows Date.getTimezoneOffset (UTC - local time).
 */
function getTimeZoneOffset(date, timeZone) {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return 0;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(date);
  } catch (error) {
    return 0;
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return (asUTC - date.getTime()) / 60000;
}

/**
 * Converts a date expressed in a specific timezone to a UTC Date instance.
 * Handles daylight-saving transitions by recalculating the offset post-adjustment.
 */
function zonedDateToUtc({ year, month, day, hour, minute, second = 0, timeZone }) {
  const baseUTCDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const initialOffset = getTimeZoneOffset(baseUTCDate, timeZone);
  const firstPass = new Date(baseUTCDate.getTime() - initialOffset * 60000);

  const verifiedOffset = getTimeZoneOffset(firstPass, timeZone);
  if (verifiedOffset !== initialOffset) {
    return new Date(baseUTCDate.getTime() - verifiedOffset * 60000);
  }

  return firstPass;
}

function formatGmtOffset(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesLabel = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  return `GMT${sign}${String(hours).padStart(2, '0')}${minutesLabel}`;
}

function formatDifferenceLabel(diffMinutes) {
  if (diffMinutes === 0) {
    return '±0h';
  }

  const sign = diffMinutes > 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesSuffix = minutes ? ` ${minutes}m` : '';
  return `${sign}${hours}h${minutesSuffix}`;
}

export default function PublicationDatePopup({ open, onClose, formState, setFormState, formError, setFormError }) {
  const t = useTranslations('common.forms.publication_schedule');
  const [dateValue, setDateValue] = useState(formState.publicationDate || '');
  const [timeValue, setTimeValue] = useState(formState.publicationTime || '');
  const lastNonQueuedStatusRef = useRef(
    formState.post_status && formState.post_status !== 'queued'
      ? formState.post_status
      : null
  );

  // Reset local inputs whenever popup opens to reflect latest form state.
  useEffect(() => {
    if (open) {
      setDateValue(formState.publicationDate || '');
      setTimeValue(formState.publicationTime || '');
    }
  }, [open, formState.publicationDate, formState.publicationTime]);

  useEffect(() => {
    if (formState.post_status && formState.post_status !== 'queued') {
      lastNonQueuedStatusRef.current = formState.post_status;
    }
  }, [formState.post_status]);

  const todayMin = useMemo(() => new Date().toISOString().split('T')[0], []);
  const userTimeZone = useMemo(() => {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolved || 'UTC';
  }, []);

  const timezoneMetrics = useMemo(() => {
    const now = new Date();
    const madridOffset = getTimeZoneOffset(now, MADRID_TIMEZONE);
    const userOffset = getTimeZoneOffset(now, userTimeZone);

    return {
      madridOffset,
      gmtLabel: formatGmtOffset(madridOffset),
      differenceLabel: formatDifferenceLabel(madridOffset - userOffset)
    };
  }, [userTimeZone]);

  const hasScheduleError = Array.isArray(formError)
    ? formError.some(error => error?.error_code === 'INVALID_PUBLICATION_SCHEDULE')
    : formError?.error_code === 'INVALID_PUBLICATION_SCHEDULE';

  const selectedSchedule = useMemo(() => {
    if (!dateValue || !timeValue) return null;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);

    if ([year, month, day, hour, minute].some(Number.isNaN)) {
      return null;
    }

    return { year, month, day, hour, minute };
  }, [dateValue, timeValue]);

  const scheduledUtcDate = useMemo(() => {
    if (!selectedSchedule) return null;

    return zonedDateToUtc({
      ...selectedSchedule,
      timeZone: MADRID_TIMEZONE
    });
  }, [selectedSchedule]);

  const isScheduleInPast = Boolean(scheduledUtcDate && scheduledUtcDate.getTime() <= Date.now());
  const shouldShowScheduleError = hasScheduleError || isScheduleInPast;
  const isSubmitDisabled = !selectedSchedule || shouldShowScheduleError;

  const clearScheduleError = () => {
    if (!setFormError || !hasScheduleError) {
      return;
    }

    setFormError(prevError => {
      if (!prevError) return prevError;

      if (Array.isArray(prevError)) {
        const filteredErrors = prevError.filter(error => error?.error_code !== 'INVALID_PUBLICATION_SCHEDULE');
        return filteredErrors.length ? filteredErrors : null;
      }

      if (prevError?.error_code === 'INVALID_PUBLICATION_SCHEDULE') {
        return null;
      }

      return prevError;
    });
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleBackgroundClick = (event) => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    handleClose();
  };

  const handleDateChange = value => {
    setDateValue(value);
    clearScheduleError();
  };

  const handleTimeChange = value => {
    setTimeValue(value);
    clearScheduleError();
  };

  const handleSubmit = () => {
    if (!selectedSchedule || !scheduledUtcDate || isScheduleInPast) {
      return;
    }

    // Compute the Madrid offset (in minutes) for the *scheduled instant*
    const scheduledOffsetMinutes = getTimeZoneOffset(scheduledUtcDate, MADRID_TIMEZONE);
    const sign = scheduledOffsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(scheduledOffsetMinutes);
    const offHH = String(Math.floor(abs / 60)).padStart(2, '0');
    const offMM = String(abs % 60).padStart(2, '0');

    // Canonical publishedAt with explicit Madrid offset (+01:00 or +02:00), not 'Z'
    const publishedAtWithOffset = `${dateValue}T${timeValue}${sign}${offHH}:${offMM}`;

    setFormState(prev => ({
      ...prev,
      publicationDate: dateValue,
      publicationTime: timeValue,
      publicationTimezone: MADRID_TIMEZONE,
      // must match the offset embedded in publishedAt
      publicationOffsetMinutes: scheduledOffsetMinutes,
      publishedAt: publishedAtWithOffset,
      post_status: 'queued'
    }));

    handleClose();
  };
  const handleReset = () => {
    setDateValue('');
    setTimeValue('');
    setFormState(prev => {
      const restoredStatus =
        prev.post_status === 'queued'
          ? lastNonQueuedStatusRef.current ?? null
          : prev.post_status;

      lastNonQueuedStatusRef.current = restoredStatus;

      return clearPublicationSchedule(prev, restoredStatus);
    });
  };

  return (
    <GenericPopup
      open={open}
      className="publication-date-popup remove-scrollbar"
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="generic-popup__header publication-date-popup__header">
        <button
          type="button"
          className="publication-date-popup__reset"
          onClick={handleReset}
          aria-label={t('reset_aria')}
        >
          <ResetIcon className="publication-date-popup__reset-icon" />
        </button>
        <Text className="publication-date-popup__title">
          {t('title')}
        </Text>
        <XIcon className="generic-popup__close" onClick={handleClose} />
      </div>

      <div className="publication-date-popup__body">
        <PublicationSchedulePicker idPrefix="publication" dateValue={dateValue} timeValue={timeValue} onDateChange={handleDateChange} onTimeChange={handleTimeChange} minimumDate={todayMin} />

        <div className="publication-date-popup__timezone">
          <Text className="publication-date-popup__timezone-title">
            {t('timezone_title', { gmtOffset: timezoneMetrics.gmtLabel })}
          </Text>
          <Text className="publication-date-popup__timezone-hint">
            {t('timezone_hint', { timeZone: userTimeZone, difference: timezoneMetrics.differenceLabel })}
          </Text>
        </div>
      </div>

      {shouldShowScheduleError && (
        <div className="publication-date-popup__error" role="alert">
          {t('past_error')}
        </div>
      )}

      <ContinueButton
        className="publication-date-popup__submit"
        onClick={handleSubmit}
        turnOff={isSubmitDisabled}
      >
        {t('set_date')}
      </ContinueButton>
    </GenericPopup>
  );
}
